import { useMsal } from "@azure/msal-react";
import InternalModal from "internal/components/internal-modal/InternalModal.comp";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { Link, useNavigate, useParams } from "react-router-dom";
import cancelIcon from "../../assets/cancel.svg";
import PaperClipIcon from "../../assets/paperclip.svg";
import {
  createComment,
  fetchInternalImages,
  getIssue,
  LinearIssueDetail,
} from "../api/linearClient";
import Markdown from "../components/Markdown/Markdown";
import { internalApiScopes } from "../msalConfig";
import styles from "../styles/issue.module.scss";
import { presentComment } from "../utils/commentPresentation";

function formatDate(raw?: string) {
  if (!raw) return "";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleString();
}

export default function InternalIssuePage() {
  const { applicationId, issueId } = useParams();
  const navigate = useNavigate();
  const { instance, accounts } = useMsal();
  const [processedDescription, setProcessedDescription] = useState<
    string | null
  >(null);
  const [attachments, setAttachments] = useState<
    { name: string; url: string }[]
  >([]);
  const [processedComments, setProcessedComments] = useState<
    Record<
      string,
      { text: string; attachments: { name: string; url: string }[] }
    >
  >({});
  const scopes = useMemo(() => internalApiScopes, []);
  const activeEmail = accounts[0]?.username;

  const [issue, setIssue] = useState<LinearIssueDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [comment, setComment] = useState<string>("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [viewerName, setViewerName] = useState<string>("");

  const refresh = useCallback(async () => {
    if (!applicationId || !issueId) return;
    try {
      const data = await getIssue(instance, scopes, applicationId, issueId);
      setIssue(data);
      console.log("Issue data:", JSON.stringify(data, null, 2));
    } finally {
      setLoading(false);
    }
  }, [applicationId, instance, issueId, scopes]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await refresh();
      } catch (e: any) {
        setError(e?.message || String(e));
        setLoading(false);
      }
    })();
  }, [refresh]);

  useEffect(() => {
    let mounted = true;
    const createdUrls: string[] = [];

    (async () => {
      if (!issue || !applicationId) return;

      const processText = async (text: string) => {
        const matches = Array.from(
          text.matchAll(/!\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)/g),
        );
        if (matches.length === 0) return { text, attachments: [] };

        const imageUrls = matches.map((m) => m[2]);
        const imageAlts = matches.map((m) => m[1]);
        const blobs = await fetchInternalImages(
          instance,
          scopes,
          applicationId,
          imageUrls,
        );

        const attachments = blobs.map((blob, i) => {
          const url = URL.createObjectURL(blob);
          createdUrls.push(url);
          return { name: imageAlts[i] || `Image ${i + 1}`, url };
        });

        let cleanText = text;
        matches.forEach((m) => {
          cleanText = cleanText.replace(m[0], "");
        });
        return { text: cleanText.trim(), attachments };
      };

      try {
        // Process Description
        const descResult = await processText(issue.description || "");
        if (mounted) {
          setProcessedDescription(descResult.text);
          setAttachments(descResult.attachments);
        }

        // Process Comments
        const commentResults: Record<
          string,
          { text: string; attachments: { name: string; url: string }[] }
        > = {};
        for (const c of issue.comments || []) {
          commentResults[c.id] = await processText(c.body);
        }
        if (mounted) setProcessedComments(commentResults);
      } catch (e) {
        console.error("Failed to process internal images:", e);
      }
    })();

    return () => {
      mounted = false;
      createdUrls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [issue, applicationId, instance, scopes]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!applicationId || !issueId) return;

    const rawComment = comment.trim();
    if (!rawComment && selectedFiles.length === 0) return;

    const body = rawComment;

    try {
      setSubmitting(true);
      await createComment(instance, scopes, applicationId, issueId, {
        body,
        files: selectedFiles,
      });
      setComment("");
      setSelectedFiles([]);
      await refresh();
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setSubmitting(false);
    }
  }

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setSelectedFiles((prev) => [...prev, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true, // Prevents clicking the whole area to open file picker
  });

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <>
    <div className={styles.backdrop} onClick={() => navigate(`/internal/app/${applicationId}`, { replace: true })} />
    <div className={styles.drawer}>
      <div className={styles.drawerHeader}>
        <div className={styles.titleContainer}>
          <div className={styles.breadcrumbs}>
            <Link
              className={styles.backLink}
              to={`/internal/app/${applicationId}`}
            >
              {applicationId?.toUpperCase()}
            </Link>
            <span className={styles.sep}>/</span>
            <span className={styles.crumb}>{issue?.identifier || issueId}</span>
          </div>
          <div className={styles.title}>{issue?.title || "Loading…"}</div>
        </div>
        <div className={styles.meta}>
          {issue && (issue.botActor?.userDisplayName || issue.creator?.name) && (
            <span className={styles.createdBy}>
              Created by{" "}
              <strong>
                {issue.botActor?.userDisplayName || issue.creator?.name}
              </strong>
            </span>
          )}
          {issue?.url && activeEmail.endsWith("@copetence.de") ? (
            <a
              className={styles.link}
              href={issue.url}
              target="_blank"
              rel="noreferrer"
            >
              Open in Linear
            </a>
          ) : null}
        </div>
        <button
          className={styles.close}
          onClick={() => navigate(`/internal/app/${applicationId}`, { replace: true })}
        >
          {cancelIcon ? (
            <img
              className={styles.closeIcon}
              src={cancelIcon}
              alt=""
              aria-hidden="true"
            />
          ) : (
            "x"
          )}
        </button>
      </div>

      {error ? <div className={styles.error}>{error}</div> : null}

      {loading ? (
        <div className={styles.loadingContainer}>
          <div className={styles.spinner}></div>
        </div>
      ) : (
        <div className={styles.drawerBody}>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>Description</div>
            <div className={styles.description}>
              {issue?.description ? (
                <Markdown text={processedDescription ?? issue.description} />
              ) : (
                <span className={styles.muted}>No description</span>
              )}
            </div>
          </div>

          {attachments.length > 0 && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Attachments</div>
              <div className={styles.attachmentsGrid}>
                {attachments.map((att, i) => (
                  <div
                    key={i}
                    className={styles.attachmentItem}
                    title={att.name}
                    onClick={() => {
                      setViewerUrl(att.url);
                      setViewerName(att.name);
                    }}
                  >
                    <img
                      src={att.url}
                      alt={att.name}
                      className={styles.attachmentPreview}
                    />
                    <div className={styles.attachmentLabel}>{att.name}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className={styles.section}>
            <div className={styles.sectionTitle}>Comments</div>

            <div className={styles.comments}>
              {(issue?.comments || []).map((c) => {
                const processed = processedComments[c.id];
                const displayBody = processed ? processed.text : c.body;
                const commentAttachments = processed
                  ? processed.attachments
                  : [];
                console.log(
                  "Rendering comment:",
                  c.id,
                  displayBody,
                  commentAttachments,
                );
                console.log("Comment attachments:", commentAttachments);
                const authorName = c.botActor?.userDisplayName || c.user?.name;
                console.log("Comment author:", authorName);
                const presented = presentComment(authorName, displayBody);

                return (
                  <div key={c.id} className={styles.comment}>
                    <div className={styles.commentHeader}>
                      <span className={styles.commentAuthor}>
                        {presented.displayAuthor}
                      </span>
                      <span className={styles.commentTime}>
                        {formatDate(c.createdAt)}
                      </span>
                    </div>
                    <div className={styles.commentBody}>
                      <Markdown text={presented.displayBody} />
                    </div>

                    {commentAttachments.length > 0 && (
                      <div className={styles.commentAttachments}>
                        <div className={styles.attachmentsGrid}>
                          {commentAttachments.map((att, i) => (
                            <div
                              key={i}
                              className={styles.attachmentItem}
                              onClick={() => {
                                setViewerUrl(att.url);
                                setViewerName(att.name);
                              }}
                            >
                              <img
                                src={att.url}
                                alt={att.name}
                                className={styles.attachmentPreview}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {issue && issue.comments.length === 0 ? (
                <div className={styles.muted}>No comments yet.</div>
              ) : null}
            </div>

            <form
              className={`${styles.composer} ${isDragActive ? styles.dragActive : ""}`}
              onSubmit={onSubmit}
              {...getRootProps()}
            >
              <input {...getInputProps()} />

              <textarea
                className={styles.textarea}
                placeholder={
                  isDragActive ? "Drop files here..." : "Write a comment"
                }
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
              />

              {selectedFiles.length > 0 && (
                <div className={styles.composerAttachmentsBox}>
                  <div className={styles.attachmentsHeader}>Attachments</div>
                  <div className={styles.composerFiles}>
                    {selectedFiles.map((file, i) => (
                      <div key={i} className={styles.composerFileItem}>
                        <span className={styles.fileName}>{file.name}</span>
                        <button
                          type="button"
                          className={styles.removeFile}
                          onClick={() => removeFile(i)}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className={styles.composerActions}>
                <label className={styles.fileLabel}>
                  <input
                    type="file"
                    multiple
                    style={{ display: "none" }}
                    onChange={(e) => {
                      if (e.target.files) {
                        onDrop(Array.from(e.target.files));
                      }
                    }}
                  />
                  <img
                    src={PaperClipIcon}
                    alt="Attach files"
                    className={styles.paperClipIcon}
                  />
                </label>
                <button
                  className="btn-primary"
                  type="submit"
                  disabled={
                    submitting ||
                    (!comment.trim() && selectedFiles.length === 0)
                  }
                >
                  {submitting ? "Sending…" : "Send"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {viewerUrl && (
        <InternalModal
          open={!!viewerUrl}
          onClose={() => setViewerUrl(null)}
          title={viewerName}
        >
          <div className={styles.viewerContent}>
            <img src={viewerUrl} alt={viewerName} />
          </div>
        </InternalModal>
      )}
    </div>
    </>
  );
}
